import ProxyImage from "@/components/ProxyImage.jsx";
import { Avatar } from "@/components/ui/Display.jsx";
import { PRODUCTIVITY, toneOf } from "@/lib/tokens.js";
import { hostOf } from "@/lib/format.js";
import { cx } from "@/lib/cx.js";

/*
 * One row in list view, from shotRow() in screenshots.js.
 *
 * The list exists for scanning a day by title rather than by picture: the
 * window title gets the flexible column and the thumbnail shrinks to a
 * postage stamp. Keystroke and click counts appear from xl up, where there is
 * room for them without squeezing the title.
 */
export default function ShotRow({ shot, onOpen, onExpired }) {
    const p = PRODUCTIVITY[shot.productivityStatus] ?? PRODUCTIVITY.unrated;

    return (
        <button
            type="button"
            onClick={onOpen}
            data-shot={shot.key}
            className="flex w-full min-w-0 items-center gap-3 border-b border-slate-100 px-3 py-2.5 text-left transition last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60"
        >
            <ProxyImage
                src={shot.src}
                alt=""
                onExpired={onExpired}
                fallbackLabel=""
                className="h-11 w-18 shrink-0 rounded border border-slate-200 dark:border-slate-700"
                imgClassName={cx(shot.blurred && "shot-blurred")}
            />

            <span className="w-20 shrink-0 font-mono text-[12.5px] text-slate-500 dark:text-slate-400">
                {shot.time}
            </span>

            <span className="flex w-44 min-w-0 shrink-0 items-center gap-2">
                <Avatar name={shot.user.name} size="sm" />
                <span className="truncate text-[13px] font-medium">{shot.user.name}</span>
            </span>

            <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px]">{shot.title || "—"}</span>
                <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                    {shot.application}
                    {shot.url ? ` · ${hostOf(shot.url)}` : ""}
                </span>
            </span>

            <span className="hidden w-32 shrink-0 font-mono text-[11.5px] text-slate-400 xl:block dark:text-slate-500">
                {shot.keystrokes} keys · {shot.mouseClicks} clicks
            </span>

            {shot.displayCount > 1 && (
                <span className="tag shrink-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    Display {shot.display}/{shot.displayCount}
                </span>
            )}

            <span className={cx("tag shrink-0", toneOf(p.tone).tag)}>{p.label}</span>

            {shot.blurred && (
                <span className="tag shrink-0 bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400">
                    Blurred
                </span>
            )}
        </button>
    );
}
